import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useThemeVersion } from "../../lib/theme.js";
import { SEVERITY_COLOR } from "../../lib/traceReview.js";

// What the Findings flow's views share (ReviewFlow.jsx draws the Sankey and
// hosts the others): the words for each node, the labels that are its
// buttons, and the canvas plumbing the per-finding views draw with.

export const CAPTION_H = 18;
export const MET = SEVERITY_COLOR.danger;
export const CAPTION = { written: "Spotted", kept: "Kept", triage: "Triaged", standard: "Need you", flagged: "Flagged by" };
// The kept path, warming step by step toward what needs a person.
export const STEP_INK = { written: "var(--severity-low)", kept: "var(--warn, #c08a2d)", triaged: "var(--severity-medium)", met: MET };
// A merged node's parts, each said in a few words.
export const PART = {
  withheld: ["Withheld", "too minor to keep"],
  context_only: ["Context only", "only in messages an earlier review covered"],
  unsupported: ["Unsupported", "the quote wasn\u2019t in the session"],
  not_triaged: ["Not reached", "Triage hasn\u2019t got to them"],
  not_loaded: ["Not loaded", "older than the findings loaded here"],
  not_met: ["No standard met", "true, but no standard asks for a person"],
  set_aside: ["Set aside", "false, or couldn\u2019t be checked"],
};
// A stacked part's label beside its segment.
export const PART_SHORT = { withheld: "too minor", context_only: "already covered", unsupported: "quote not in session" };

export const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;
export const pct = (share) => (share == null ? "" : share > 0 && share < 0.01 ? "<1%" : `${Math.round(share * 100)}%`);

const LABEL_CLASS = "absolute cursor-pointer truncate rounded-[2px] border-0 bg-[color-mix(in_srgb,var(--plot-bg)_86%,transparent)] px-[3px] text-left leading-[16px] whitespace-nowrap transition-opacity";

// A node's label: the count of what a step carried on (the column names it),
// or "N filtered out" for what it let go. The label is the node's button; the
// mark answers the pointer the same way. `delay` staggers it in behind a
// view that draws itself in.
export function NodeLabel({ node, lay, left, maxWidth, dim, onPoint, onLeave, onOpen, hidden = false, delay = 0 }) {
  const drop = node.kind === "drop";
  return (
    <button
      type="button"
      data-flow-node={node.id}
      aria-label={`${node.label}: ${plural(node.count, "finding")}${node.share != null ? `, ${pct(node.share)}` : ""}`}
      onMouseEnter={onPoint}
      onMouseLeave={onLeave}
      onFocus={onPoint}
      onBlur={onLeave}
      onClick={onOpen}
      className={`${LABEL_CLASS} ${hidden ? "opacity-0" : dim ? "opacity-40" : ""}`}
      style={{ left, top: CAPTION_H + lay.labelTop, maxWidth, transitionDelay: delay ? `${delay}ms` : undefined }}
    >
      {drop ? (
        <span className="text-[11px] text-[color:var(--muted)]">{node.count.toLocaleString()} {node.label.toLowerCase()}</span>
      ) : node.flag ? (
        <span className="text-[11px] text-[color:var(--ink)]"><span className="text-[13px] font-medium tabular-nums" style={{ color: MET }}>{node.count.toLocaleString()}</span> {node.label}</span>
      ) : (
        <span className="text-[13px] font-medium tabular-nums" style={{ color: node.id === "met" ? MET : "var(--ink)" }}>{node.count.toLocaleString()}</span>
      )}
    </button>
  );
}

// One part of a stacked node, labelled beside its segment; it points at and
// opens the node it belongs to.
export function PartLabel({ node, seg, left, maxWidth, dim, onPoint, onLeave, onOpen, hidden = false, delay = 0 }) {
  return (
    <button
      type="button"
      data-flow-node={node.id}
      data-flow-part={seg.id}
      aria-label={`${node.label}, ${PART_SHORT[seg.id] || seg.id}: ${plural(seg.count, "finding")}`}
      onMouseEnter={onPoint}
      onMouseLeave={onLeave}
      onFocus={onPoint}
      onBlur={onLeave}
      onClick={onOpen}
      className={`${LABEL_CLASS} ${hidden ? "opacity-0" : dim ? "opacity-40" : ""}`}
      style={{ left, top: CAPTION_H + seg.labelTop, maxWidth, transitionDelay: delay ? `${delay}ms` : undefined }}
    >
      <span className="text-[11px] text-[color:var(--muted)]">{seg.count.toLocaleString()} {PART_SHORT[seg.id] || PART[seg.id]?.[0].toLowerCase() || seg.id}</span>
    </button>
  );
}

// Labels sit at their wanted top, pushed apart where neighbours would overlap
// and kept inside [0, height]. Mutates and returns `items` ({ want, h, top }).
export function relax(items, height, gap = 2) {
  const sorted = [...items].sort((a, b) => a.want - b.want);
  let y = -Infinity;
  for (const it of sorted) { it.top = Math.max(it.want, y + gap, 0); y = it.top + it.h; }
  let floor = height;
  for (let i = sorted.length - 1; i >= 0; i--) {
    const it = sorted[i];
    if (it.top + it.h > floor) it.top = Math.max(0, floor - it.h);
    floor = it.top - gap;
  }
  return items;
}

// -- canvas ----------------------------------------------------------------------

export const reducedMotion = () => typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// CSS colours resolved against the plot, for a canvas that cannot read
// var(): -> { key: "rgb(...)" }. Re-read when the theme flips.
export function useInks(ref, inks) {
  const theme = useThemeVersion();
  const [out, setOut] = useState(null);
  const spec = JSON.stringify(inks);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const probe = document.createElement("span");
    probe.style.display = "none";
    el.appendChild(probe);
    const next = {};
    for (const [k, v] of Object.entries(inks)) {
      probe.style.color = "";
      probe.style.color = v;
      next[k] = getComputedStyle(probe).color || "rgb(128, 128, 128)";
    }
    probe.remove();
    setOut(next);
  }, [theme, spec]);
  return out;
}

// A resolved rgb()/rgba() colour at an alpha (multiplied into its own).
export function alpha(color, a) {
  const m = /rgba?\(([^)]+)\)/.exec(color || "");
  if (!m) return color;
  const [r, g, b, own = 1] = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, own * a))})`;
}

// A canvas sized to its box in device pixels: -> [ref, ctx-getter].
export function useCanvas(width, height) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.max(1, Math.round(width * dpr));
    cv.height = Math.max(1, Math.round(height * dpr));
  }, [width, height]);
  const ctx = () => {
    const cv = ref.current;
    if (!cv) return null;
    const c = cv.getContext("2d");
    const dpr = cv.width / Math.max(1, width);
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, width, height);
    return c;
  };
  return [ref, ctx];
}

// Drives a view's one-time draw-in: calls `paint(progress)` each frame for
// `ms` from when `signature` changes, then once at 1. Reduced motion, a
// hidden tab or no signature paints the end state straight away.
export function useReveal(signature, ms, paint) {
  const paintRef = useRef(paint);
  paintRef.current = paint;
  const done = useRef(false);
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    done.current = false;
    setSettled(false);
    if (!signature || reducedMotion() || document.hidden) {
      done.current = true;
      setSettled(true);
      paintRef.current(1);
      return undefined;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / ms);
      paintRef.current(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else { done.current = true; setSettled(true); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [signature, ms]);
  return { settled, done };
}

// A client-space rect for a box in the plot, for the house tooltip's anchor.
export function clientRect(el, x, y, w, h) {
  const r = el.getBoundingClientRect();
  return { left: r.left + x, top: r.top + y, right: r.left + x + w, bottom: r.top + y + h, width: w, height: h };
}

// -- geometry ------------------------------------------------------------------

// Strand and grain geometry the views share: a cubic sampled into a flat
// [x, y, x, y, ...] list, and the y a sampled path crosses x at.
export function sampleCubic(out, x0, y0, x1, y1, x2, y2, x3, y3, n, skipFirst) {
  for (let k = skipFirst ? 1 : 0; k <= n; k++) {
    const s = k / n;
    const u = 1 - s;
    out.push(u * u * u * x0 + 3 * u * u * s * x1 + 3 * u * s * s * x2 + s * s * s * x3,
      u * u * u * y0 + 3 * u * u * s * y1 + 3 * u * s * s * y2 + s * s * s * y3);
  }
}
export function sampleAcross(out, xa, ya, xb, yb, n, skipFirst, k = 0.5) {
  const d = (xb - xa) * k;
  sampleCubic(out, xa, ya, xa + d, ya, xb - d, yb, xb, yb, n, skipFirst);
}
export function yAt(p, x, last = p.length / 2 - 1) {
  if (x < p[0] || x > p[last * 2]) return null;
  let lo = 0;
  let hi = last;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (p[m * 2] <= x) lo = m; else hi = m; }
  const x0 = p[lo * 2];
  const x1 = p[hi * 2];
  const r = (x - x0) / ((x1 - x0) || 1);
  return p[lo * 2 + 1] + (p[hi * 2 + 1] - p[lo * 2 + 1]) * r;
}
// Draws a sampled path from point a to b, cut at the draw-in front.
export function tracePath(ctx, p, a, b, front) {
  let x = p[a * 2];
  let y = p[a * 2 + 1];
  if (x > front) return;
  ctx.moveTo(x, y);
  for (let k = a + 1; k <= b; k++) {
    const nx = p[k * 2];
    const ny = p[k * 2 + 1];
    if (nx > front) { const r = (front - x) / ((nx - x) || 1); ctx.lineTo(x + (nx - x) * r, y + (ny - y) * r); return; }
    ctx.lineTo(nx, ny);
    x = nx;
    y = ny;
  }
}
export const hash = (i, k = 0) => { const x = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453; return x - Math.floor(x); };
